"use client";

import { DEFAULT_DATE_FORMAT, type DateFormat } from "@/features/profile-setup/_logic/constants";
import { DATE_FORMAT_LOCALES, formatDate, type DateInput, type DateLocale } from "@/lib/date-format/date-format";
import { useContext, useMemo } from "react";
import { DateFormatContext } from "./DateFormatContext";

/** What `useDateFormat` returns. Identity is stable while the format is unchanged. */
export interface UseDateFormatResult {
  /** The user's format, also the dayjs pattern for date-picker inputs: "MM/DD/YYYY", "DD/MM/YYYY", or "YYYY-MM-DD". */
  dateFormat: DateFormat;
  /** Intl locale for the format; pass it wherever a call site used "en-US". */
  locale: DateLocale;
  /** Formats a date in the user's order. Options default to "Oct 20, 2026" / "20 Oct 2026". */
  formatDate: (value: DateInput, options?: Intl.DateTimeFormatOptions) => string;
}

/**
 * Returns the current user's date format and formatters for it. Outside a
 * `DateFormatProvider` (admin, public pages, emails) it returns {@link DEFAULT_DATE_FORMAT}.
 *
 * @returns The format, its Intl locale, and a `formatDate` bound to that locale.
 */
export function useDateFormat(): UseDateFormatResult {
  const dateFormat = useContext(DateFormatContext) ?? DEFAULT_DATE_FORMAT;

  return useMemo(() => {
    const locale = DATE_FORMAT_LOCALES[dateFormat];
    return {
      dateFormat,
      locale,
      formatDate: (value: DateInput, options?: Intl.DateTimeFormatOptions) => formatDate(locale, value, options),
    };
  }, [dateFormat]);
}
